/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { user } from '../../redux/user';
import Select from '../../components/form/Select';
import trashRedIcon from '../../assets/images/trash-red-icon.svg';
import CustomNumberInput from '../../components/CustomNumberInput/CustomNumberInput';
import { GuaranteedIcon } from '../../components/icons';
import utils from '../../utils/utils';
import Tooltip from '../../components/Tooltip/Tooltip';
import Checkbox from '../../components/form/Checkbox';

const SLOT_LIMIT = 20;
const SLOT_MIN_LIMIT = 1;

const getFieldLabel = (showOnlyFirstRowLabels, shiftIndex, label) => {
	if (!showOnlyFirstRowLabels) {
		return label;
	}
	if (shiftIndex === 0) {
		return label;
	}
	return '';
};

const ShiftRequired = (props) => {
	const { timezone, permissions, permissionsByFacility } = useSelector(user);
	const [shiftStructureOptions, setShiftStructureOptions] = useState([]);
	const [structureIsOrientation, setStructureIsOrientation] = useState(false);

	const { shiftStructureId, unitId, numberOfProviders, resourceType, isGuaranteed, id } = props.shift;
	const {
		availableShiftStructures,
		date,
		errors,
		isFlex,
		showOnlyFirstRowLabels,
		shiftIndex,
		isGlobalCalendar,
		currentFacility,
		showUnits
	} = props;
	const dateSelected = moment.tz(date, timezone);

	const unitOptions = useMemo(() => {
		if (props.units?.length > 0) {
			const options = props.units.map((unit) => ({ label: unit.unit_name, value: unit.id }));
			return [{ value: null, label: '- Remove Unit -' }, ...options];
		}

		return [];
	}, [props.units]);

	const hasGuaranteePermission = useMemo(() => {
		if (isGlobalCalendar) {
			const facilityPermissions = permissionsByFacility[currentFacility];
			return utils.isAuthorizedExact('PORTAL.GUARANTEE', facilityPermissions);
		}
		return utils.isAuthorizedExact('PORTAL.GUARANTEE', permissions);
	}, [permissions, currentFacility, isGlobalCalendar, permissionsByFacility]);

	/* returns unique resource types values for dropdown selection */
	const getResourceTypeOptions = useCallback(() => {
		const added = {};
		const uniqueResourceTypes = [];

		availableShiftStructures.forEach((shiftStructure) => {
			if (!added[shiftStructure.resource_type]) {
				added[shiftStructure.resource_type] = true;
				uniqueResourceTypes.push({
					value: shiftStructure.resource_type,
					label: shiftStructure.resource_type
				});
			}
		});

		return uniqueResourceTypes;
	}, [availableShiftStructures]);

	/* we need to get the new available shift options in the second dropdown every time either the resource type changes
	 * or the available shift structures are changing
	 * */
	useEffect(() => {
		const structures = availableShiftStructures
			.filter((structure) => {
				const timeConstraint = isFlex
					? utils.isStructureEndingSoon(structure, timezone, dateSelected, 0)
					: utils.isStructureAlreadyStarted(structure, timezone, dateSelected);

				return structure.resource_type === resourceType && !timeConstraint;
			})
			.map((structure) => {
				const fromTime = moment.utc(structure.from_time, 'HH:mm:ss').format('h:mm A');
				const toTime = moment.utc(structure.to_time, 'HH:mm:ss').format('h:mm A');
				return {
					value: structure.id,
					label: structure.is_orientation ? `Orientation ${fromTime} - ${toTime}` : `${fromTime} - ${toTime}`
				};
			});
		setShiftStructureOptions(structures);
	}, [resourceType, availableShiftStructures, timezone]); // eslint-disable-line react-hooks/exhaustive-deps

	const updateShift = (patchObject) => {
		props.updateShift(props.shiftGroupIndex, props.shiftIndex, patchObject, id);
	};

	const handleShiftStructureChange = (value) => {
		const _shiftStructure = availableShiftStructures.find(
			(structure) => value.structure === structure.id
			// eslint-disable-next-line camelcase
		);
		const _shiftStructureIsOrientation = _shiftStructure ? _shiftStructure.is_orientation : null;
		setStructureIsOrientation(_shiftStructureIsOrientation);
		updateShift({
			shiftStructureId: value.structure,
			isGuaranteed: _shiftStructureIsOrientation ? false : isGuaranteed
		});
	};

	const handleUnitChange = (value) => {
		let unitName = '';
		if (value.unitId) {
			unitName = unitOptions.find((option) => option.value === value.unitId).label;
		}

		updateShift({
			unitId: value.unitId,
			unitName
		});
	};

	useEffect(() => {
		if (shiftStructureId) {
			handleShiftStructureChange({ structure: shiftStructureId });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [date]);

	/* every time the resource type changes, we need to reset the current shift shiftStructureId */
	const handleResourceTypeChange = (value) => {
		updateShift({
			resourceType: value.type,
			shiftStructureId: null
		});
	};

	const removeShift = () => {
		props.removeShift(props.shiftGroupIndex, props.shiftIndex, id);
	};

	const handleProvidersChange = (currentValue) => {
		let value = null;

		if (Number.isInteger(parseInt(currentValue, 10))) {
			value = parseInt(currentValue, 10);
		}

		updateShift({ numberOfProviders: value });
	};

	const handleIsGuaranteed = () => updateShift({ isGuaranteed: !isGuaranteed });

	const handleBlur = (e) => {
		const parsedValue = parseInt(e.target.value, 10);

		if (Number.isInteger(parsedValue) && parsedValue > SLOT_LIMIT) {
			updateShift({ numberOfProviders: SLOT_LIMIT });
		} else if (!Number.isInteger(parsedValue)) {
			updateShift({ numberOfProviders: null });
		} else if (parsedValue < SLOT_MIN_LIMIT) {
			updateShift({ numberOfProviders: SLOT_MIN_LIMIT });
		}
	};

	const selectorClasses = useMemo(() => {
		const selectorWithoutTitle = showOnlyFirstRowLabels && shiftIndex !== 0;
		const showingErrorResourceType = showOnlyFirstRowLabels && shiftIndex === 0 && errors.resourceType;
		const showingErrorShiftStructureId = showOnlyFirstRowLabels && shiftIndex === 0 && errors.shiftStructureId;
		const showingErrorUnitId = showOnlyFirstRowLabels && shiftIndex === 0 && errors.unitId;
		const showingErrorNumberOfProviders = showOnlyFirstRowLabels && shiftIndex === 0 && errors.numberOfProviders;

		return {
			resourceType: classNames('shift-selector', {
				'selector-without-title': selectorWithoutTitle && !errors.resourceType,
				'showing-error': showingErrorResourceType,
				'showing-unit': showUnits
			}),
			shiftStructureId: classNames('shift-selector', {
				'selector-without-title': selectorWithoutTitle && !errors.shiftStructureId,
				'showing-error': showingErrorShiftStructureId,
				'showing-unit': showUnits
			}),
			unitId: classNames('shift-selector', {
				'selector-without-title': selectorWithoutTitle && !errors.unitId,
				'showing-error': showingErrorUnitId,
				'showing-unit': showUnits
			}),
			numberOfProviders: classNames('shift-selector', 'provider-selector', {
				'selector-without-title': selectorWithoutTitle && !errors.numberOfProviders,
				'showing-error': showingErrorNumberOfProviders
			})
		};
	}, [showOnlyFirstRowLabels, shiftIndex, errors, showUnits]);

	const guaranteedTooltipText = useMemo(() => {
		if (props.isHybrid) {
			return 'Guaranteed Shift: Guaranteed Shifts don’t apply to Flex professionals. By guaranteeing a shift, you agree to pay the ShiftMed professional even if you cancel. Guaranteed Shifts are picked up more often.';
		}

		return 'Guaranteed Shift';
	}, [props.isHybrid]);

	return (
		<div>
			<div className="shift-options">
				<div className={selectorClasses.resourceType}>
					<Select
						label={getFieldLabel(props.showOnlyFirstRowLabels, props.shiftIndex, 'Shift Type')}
						classNamePrefix="select-value"
						className="select-facility-request-shift"
						name="type"
						value={resourceType}
						hasError={errors.resourceType}
						placeholder="Select..."
						handleChange={handleResourceTypeChange}
						options={getResourceTypeOptions()}
					/>
					{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
					<label className={classNames('required-label', { show: errors.resourceType })}>Required</label>
				</div>
				<div className={selectorClasses.shiftStructureId}>
					<Select
						label={getFieldLabel(props.showOnlyFirstRowLabels, props.shiftIndex, 'Shift')}
						classNamePrefix="select-value"
						className="select-facility-request-shift"
						name="structure"
						placeholder="Select..."
						value={shiftStructureId}
						hasError={errors.shiftStructureId}
						handleChange={handleShiftStructureChange}
						options={shiftStructureOptions}
					/>
					{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
					<label className={classNames('required-label', { show: errors.shiftStructureId })}>Required</label>
				</div>
				{showUnits && (
					<div className={selectorClasses.unitId}>
						<Select
							label={getFieldLabel(props.showOnlyFirstRowLabels, props.shiftIndex, 'Unit')}
							classNamePrefix="select-value"
							className="select-facility-request-shift"
							containerClass="optional-label"
							name="unitId"
							placeholder="Select..."
							value={unitId}
							hasError={errors.unitId}
							handleChange={handleUnitChange}
							options={unitOptions}
						/>
					</div>
				)}

				<div className={selectorClasses.numberOfProviders}>
					{((props.showOnlyFirstRowLabels && props.shiftIndex === 0) || !props.showOnlyFirstRowLabels) && (
						<p className="provider-label"># of Providers</p>
					)}
					<CustomNumberInput
						className={classNames('provider-input', { 'has-error': errors.numberOfProviders })}
						value={numberOfProviders}
						onChange={handleProvidersChange}
						onBlur={handleBlur}
						min={SLOT_MIN_LIMIT}
						max={SLOT_LIMIT}
					/>
					{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
					<label className={classNames('required-label', { show: errors.numberOfProviders })}>Required</label>
				</div>
				{!props.isFlex && (
					<div
						className={classNames('actions-container', {
							'no-title': props.showOnlyFirstRowLabels && props.shiftIndex === 0
						})}
					>
						{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
						<label className="guaranteed-shift">
							<Checkbox
								className="guaranteed-checkbox"
								disabled={structureIsOrientation || !hasGuaranteePermission}
								checked={isGuaranteed}
								handleChange={handleIsGuaranteed}
							/>
							<Tooltip label={guaranteedTooltipText} placement="top">
								<GuaranteedIcon color="#00B1BE" height={20} width={20} />
							</Tooltip>
						</label>
						{props.allowRemove && (
							<div className={classNames('remove-section')} onClick={removeShift}>
								<img className="remove-img" src={trashRedIcon} alt="remove" />
							</div>
						)}
					</div>
				)}
				{props.allowRemove && props.isFlex && (
					<div
						className={classNames('actions-container', {
							'no-title': props.showOnlyFirstRowLabels && props.shiftIndex === 0
						})}
					>
						<div
							className={classNames('remove-section', {
								'with-guaranteed': !props.isFlex,
								'with-hybrid-guaranteed': props.isHybrid
							})}
							onClick={removeShift}
						>
							<img className="remove-img" src={trashRedIcon} alt="remove" />
						</div>
					</div>
				)}
			</div>
			{props.showDivider && <div className="divider-item" />}
		</div>
	);
};

export default memo(ShiftRequired);
